import React from 'react';
import { LegalSectionBody } from '../components/legal/LegalSectionBody';

export const TERMS_SUMMARY_CARDS = [
  {
    icon: '🔞',
    title: '18+ Only',
    desc: 'StrapIn is for adults only. Minors are not permitted under any circumstances.',
  },
  {
    icon: '🤝',
    title: 'Respect Others',
    desc: 'Treat people with dignity. Queer spaces should feel safer — not scarier.',
  },
  {
    icon: '🚫',
    title: 'No Harassment',
    desc: 'Bullying, threats, stalking, hate speech, and outing are never allowed.',
  },
  {
    icon: '🛡️',
    title: 'No Scams',
    desc: 'Fake profiles, impersonation, fraud, and financial scams are prohibited.',
  },
  {
    icon: '💜',
    title: 'Consent Matters',
    desc: 'All interactions — online and offline — must be consensual between adults.',
  },
  {
    icon: '🔒',
    title: 'Privacy Respect',
    desc: 'Private chats and media deserve respect. Do not share without consent.',
  },
  {
    icon: '✓',
    title: 'Verification Rules',
    desc: 'Verification helps trust — it does not guarantee behavior or safety.',
  },
  {
    icon: '🌈',
    title: 'Community Safety',
    desc: 'Report harmful behavior. We review reports and take action when needed.',
  },
];

function section(title, icon, bodyProps) {
  return {
    title,
    icon,
    content: <LegalSectionBody {...bodyProps} />,
  };
}

export const TERMS_SECTIONS = [
  section('Eligibility', '🔞', {
    intro:
      'StrapIn is built for adults. You must be at least 18 years old — or the age of legal majority where you live, if higher — to create or use an account.',
    paragraphs: [
      'By using StrapIn, you confirm that you meet this age requirement and that all information you provide about your age is truthful.',
      'We have zero tolerance for minors on the platform. If we learn or reasonably suspect that an account belongs to someone under 18, we may immediately suspend or remove the account and delete associated data, subject to legal obligations.',
      'You are responsible for ensuring you are legally permitted to use a dating and social connection service in your location.',
    ],
    bullets: [
      'No minors — ever',
      'No accounts created on behalf of someone under 18',
      'Suspected underage accounts may be removed without prior notice',
      'We may report serious child-safety concerns to appropriate authorities where required by law',
    ],
  }),
  section('Account Rules', '👤', {
    intro: 'Your account belongs to you — and only you.',
    bullets: [
      'One account per person. Do not create duplicate, backup, or shared accounts.',
      'Use accurate information. Misleading names, photos, ages, or identity details are not allowed.',
      'Protect your login credentials. You are responsible for activity on your account.',
      'Do not share your account with others or allow anyone else to use it.',
      'Do not use bots, scripts, or automated tools to access or manipulate StrapIn.',
      'Notify us promptly if you believe your account has been compromised.',
    ],
  }),
  section('Trust & Safety Investigations', '🔎', {
    intro:
      'You agree that StrapIn may use limited account, network, device, interaction, report, and Safe Meet records to protect users and investigate suspected scams, fraud, impersonation, account compromise, abuse, or serious offline incidents.',
    bullets: [
      'Important security events may include account registration, login, message sends, reports, and Safe Meet actions',
      'Technical evidence may include server-observed IP addresses, timestamps, app details, and pseudonymous device or session identifiers',
      'We may compare technical signals across accounts to identify possible coordinated or repeat abuse',
      'A shared IP or device signal is supporting information only and does not by itself prove identity, misconduct, or exact location',
      'We may preserve specifically identified records while a safety investigation, dispute, or valid legal request is active',
      'Attempting to evade a suspension or ban through new accounts, devices, or credentials violates this Agreement',
    ],
    paragraphs: [
      'These systems do not authorize public identification, live tracking, or disclosure of another user’s technical information. Details about collection, retention, access, and your rights appear in the Privacy Policy.',
    ],
  }),
  section('Community Conduct', '🌈', {
    intro:
      'StrapIn exists to help queer people connect with more privacy and more respect. That only works when everyone behaves responsibly.',
    paragraphs: [
      'Queer safety is not generic. Outing someone — revealing their identity, orientation, or private information without consent — can cause real harm. It is strictly prohibited.',
      'Blackmail, coercion, and threats are never tolerated. Neither is using someone’s closeted status, photos, or messages against them.',
    ],
    subsections: [
      {
        heading: 'Not allowed on StrapIn',
        bullets: [
          'Harassment, bullying, intimidation, or repeated unwanted contact',
          'Outing users or threatening to expose someone’s identity or orientation',
          'Threats, violence, or encouragement of harm',
          'Stalking or persistent contact after someone has said no',
          'Hate speech or attacks based on identity, orientation, gender, race, disability, or religion',
          'Blackmail, extortion, or coercion of any kind',
          'Spam, scams, fraud, or misleading commercial activity',
          'Fake profiles, impersonation, or pretending to be someone else',
          'Misleading identity, age, or intent',
        ],
      },
    ],
  }),
  section('Screenshot & Privacy Rules', '📵', {
    intro:
      'Private queer conversations deserve protection. StrapIn actively discourages and restricts screenshots and screen recordings within the app.',
    paragraphs: [
      'No technology can guarantee perfect prevention on every device or in every situation. Protections may vary by platform, OS version, or external recording methods.',
      'You may not attempt to bypass, disable, or circumvent screenshot or privacy protections.',
      'You may not record, capture, download, redistribute, or share private chats, albums, or media without clear consent from everyone involved.',
      'Sharing private content outside StrapIn without consent — including to expose, shame, or blackmail someone — is a serious violation and may result in permanent removal.',
    ],
  }),
  section('Verification Rules', '✓', {
    intro:
      'StrapIn offers identity review to help build trust — not to promise that any person is safe, honest, or well-intentioned.',
    subsections: [
      {
        heading: 'Public verification',
        paragraphs: [
          'Some users choose to display a verification badge on their profile after review. This signals that StrapIn has reviewed submitted materials — not that we endorse the person or guarantee future behavior.',
        ],
      },
      {
        heading: 'Private / discreet verification',
        paragraphs: [
          'Some users need trust without public visibility. Private verification is reviewed internally and is not shown as a public badge. Verification media is never public.',
        ],
      },
      {
        heading: 'What verification may include',
        bullets: [
          'A clear selfie',
          'An angled selfie (as requested)',
          'A short liveness video to confirm you are a real person',
        ],
      },
      {
        heading: 'What verification does not mean',
        bullets: [
          'It is not a guarantee of behavior, character, or intentions',
          'It is not a promise of offline safety',
          'It does not replace your own judgment when meeting people',
        ],
      },
      {
        heading: 'StrapIn may',
        bullets: [
          'Approve, reject, or request a new submission',
          'Revoke verification at any time',
          'Use verification review for fraud and impersonation prevention',
        ],
      },
    ],
    paragraphs: [
      'Verification media is reviewed only by authorized moderation staff under strict access controls. See our Privacy Policy for how verification data is handled.',
    ],
  }),
  section('Sexual Content & Consent', '💜', {
    intro:
      'StrapIn is an adult platform. Consensual adult self-expression between adults is allowed within community standards and applicable law.',
    subsections: [
      {
        heading: 'Never allowed',
        bullets: [
          'Any content involving minors or anyone appearing to be under 18',
          'Coercion, assault, trafficking, or exploitation',
          'Hidden recording, revenge content, or non-consensual sharing',
          'Content shared without the consent of everyone depicted',
          'Material used to threaten, blackmail, or harm someone',
        ],
      },
    ],
    paragraphs: [
      'Consent must be ongoing and mutual — online and offline. If someone withdraws consent, respect it immediately.',
      'We may remove content and restrict accounts that violate these rules, including where required for legal compliance or user safety.',
    ],
  }),
  section('Reporting & Moderation', '🚨', {
    intro:
      'If something feels wrong, report it. StrapIn provides in-app reporting and blocking tools so you can flag concerns from profiles and chats.',
    paragraphs: [
      'Reports are reviewed by our moderation team. We may request additional information, investigate patterns, and take action based on our policies and applicable law.',
      'In serious cases — including threats, blackmail, scams, impersonation, or child-safety concerns — we may act immediately, including suspension or permanent removal, with or without prior warning.',
    ],
    bullets: [
      'Warnings, feature restrictions, or temporary suspension',
      'Content removal',
      'Permanent account bans',
      'Cooperation with valid legal requests where required',
    ],
  }),
  section('Offline Interactions', '📍', {
    intro:
      'StrapIn helps people connect online. We cannot control — and do not guarantee — what happens when users meet in person.',
    paragraphs: [
      'You are solely responsible for your decisions, safety, and conduct during offline meetings, travel, and any interaction outside the app.',
      'Use your judgment. Meet in public when you can, tell someone you trust, and leave if something feels off. StrapIn is not liable for conduct, harm, or disputes between users offline.',
      'If you are in immediate danger, contact local emergency services. You can also report the user in-app and reach our safety team.',
    ],
  }),
  section('User Content', '📝', {
    intro: 'You own the content you create and share on StrapIn.',
    paragraphs: [
      'To operate the service — including displaying your profile, delivering messages, running discovery features, and enforcing safety — you grant StrapIn a limited, non-exclusive, worldwide license to host, store, reproduce, display, and process your content only as needed to provide and improve the app.',
      'This license ends when your content is deleted from our systems, subject to reasonable backup cycles, legal retention requirements, and safety investigations described in our Privacy Policy.',
      'You represent that you have the rights to share your content and that it does not violate these Terms or applicable law.',
    ],
  }),
  section('Subscriptions & Payments', '💳', {
    intro:
      'StrapIn may offer paid features or subscriptions in the future. When you purchase through the Apple App Store or Google Play Store, their billing terms also apply.',
    bullets: [
      'Prices and features may change with notice where required',
      'Subscriptions may renew automatically unless cancelled through your app store account settings',
      'Refunds are handled according to app store policies and applicable law',
      'Chargebacks or payment abuse may result in account restriction',
      'We do not store full payment card details — purchases are processed by the app store provider',
    ],
  }),
  section('Account Suspension & Termination', '⛔', {
    intro:
      'We may suspend, restrict, or permanently remove accounts that violate these Terms, create safety risks, or harm the community.',
    bullets: [
      'Policy violations, harassment, scams, or impersonation',
      'Circumventing privacy or screenshot protections',
      'Suspected fraud, spam, or automated abuse',
      'Legal requirements or valid law-enforcement requests',
      'Extended inactivity, where permitted by law',
    ],
    paragraphs: [
      'You may delete your account at any time through in-app settings or our account deletion process. Some data may be retained for safety, fraud prevention, or legal compliance as described in our Privacy Policy.',
    ],
  }),
  section('Limitation of Liability', '⚖️', {
    intro:
      'StrapIn is provided on an “as is” and “as available” basis. We work hard to build a safer queer platform — but we cannot promise perfect outcomes.',
    paragraphs: [
      'To the fullest extent permitted by law, StrapIn and its operators are not liable for indirect, incidental, special, consequential, or punitive damages arising from your use of the service.',
      'We do not guarantee matches, compatibility, user behavior, verification accuracy, uninterrupted service, or offline safety.',
      'Our total liability for any claim relating to the service is limited to the greater of (a) amounts you paid us in the twelve months before the claim, or (b) a reasonable minimum permitted by applicable law.',
      'Some jurisdictions do not allow certain limitations — in those cases, our liability is limited to the maximum extent permitted by law.',
    ],
  }),
  section('Governing Law & Disputes', '🌍', {
    intro:
      'StrapIn is operated from India. These Terms are governed by the laws of India, without regard to conflict-of-law principles, except where mandatory local consumer protections apply.',
    paragraphs: [
      'If you access StrapIn from another country, you are responsible for complying with local laws. Where required, you may have additional rights under laws in your region.',
      'We encourage resolving concerns informally first — contact us at terms@strapinapp.com. If a dispute cannot be resolved, courts in India may have jurisdiction, subject to mandatory rights in your location.',
    ],
  }),
  section('Law Enforcement Requests', '🏛️', {
    intro:
      'We take user privacy seriously — including for closeted and queer users. We do not volunteer user data to authorities.',
    paragraphs: [
      'We respond to law enforcement and legal requests only when they are valid, legally sufficient, and scoped appropriately under applicable law.',
      'We may challenge overbroad, unclear, or improper requests. Where permitted, we aim to notify users before disclosing information, unless prohibited or where notice could create harm.',
      'A preservation request may temporarily prevent deletion of specifically identified safety or security records, but disclosure still requires sufficient legal authority.',
      'Child-safety and imminent-harm situations may require expedited action and reporting as required by law.',
    ],
  }),
];

export const TERMS_LAST_UPDATED = 'August 9, 2026';
